import { Dimensions, Platform } from "react-native";
import StyleSheet from "react-native-media-query";

import {
  FONTS,
  SPACES,
  BREAKPOINT_VALUES,
  FONT_WEIGHT,
  getBrandingColors,
} from "./constants";
import Config from "react-native-config";

const getBrandName = () => {
  const companyName =
    (Platform.OS == "web"
      ? process.env.REACT_APP_POLICY_COMPANY
      : Config.REACT_APP_POLICY_COMPANY) ?? "typtap";

  return companyName?.toUpperCase();
};

const { width, height } = Dimensions.get("window");
export const isTablet = width >= BREAKPOINT_VALUES[768];
export const isDesktop = width >= BREAKPOINT_VALUES[1024];

export const getResponsiveSize = (
  mobile: number,
  tablet: number,
  desktop: number
) => {
  if (isDesktop) return desktop;
  if (isTablet) return tablet;
  return mobile;
};

type StyleConfig = {
  mobile: Record<string, any>;
  web: Record<string, any>;
};

export const createPlatformStyles = (styleConfig: StyleConfig) => {
  const platform = Platform.OS;
  const styles = platform === "web" ? styleConfig.web : styleConfig.mobile;
  return StyleSheet.create(styles);
};

// Theme Construction
export const theme = {
  height,
  width,
  colors: { ...getBrandingColors(getBrandName()) },
  border: {
    none: 0,
    thin: 1,
    medium: 2,
    thick: 4,
  },
  spacing: {
    none: SPACES[0],
    micro: SPACES[1],
    xxss: SPACES[2],
    xxs: SPACES[4],
    xs: SPACES[6],
    sm: SPACES[8],
    ssm: SPACES[10],
    reg: SPACES[12],
    cs: SPACES[14],
    md: SPACES[16],
    mmd: SPACES[18],
    lg: SPACES[20],
    sxlg: SPACES[22],
    xlg: SPACES[24],
    xl: SPACES[28],
    xxl: SPACES[32],
    xxl2: SPACES[36],
    xxl3: SPACES[40],
    xxl4: SPACES[44],
    xxl5: SPACES[48],
    xxxl: SPACES[64],
    xxxxl: SPACES[96],
    xxxxxl: SPACES[128],
  },
  margin: {
    none: SPACES[0],
    micro: SPACES[1],
    xxss: SPACES[2],
    xxs: SPACES[4],
    xs: SPACES[6],
    sm: SPACES[8],
    ssm: SPACES[10],
    reg: SPACES[12],
    cs: SPACES[14],
    md: SPACES[16],
    mmd: SPACES[18],
    lg: SPACES[20],
    xlg: SPACES[24],
    xl: SPACES[28],
    xxl: SPACES[32],
    xxl2: SPACES[36],
    xxl3: SPACES[40],
    xxl4: SPACES[44],
    xxl5: SPACES[48],
    xxxl: SPACES[64],
    xxxxl: SPACES[96],
    xxxxxl: SPACES[128],
  },
  radius: {
    xxss: SPACES[4],
    xxs: SPACES[6],
    xs: SPACES[8],
    sm: SPACES[10],
    ssm: SPACES[12],
    reg: SPACES[14],
    cs: SPACES[16],
    md: SPACES[18],
    mmd: SPACES[20],
    lg: SPACES[24],
    xlg: SPACES[28],
    xl: SPACES[32],
    xxl: SPACES[64],
  },
  fontFamily: {
    robotoMedium: FONTS.robotoMedium,
    robotoRegular: FONTS.robotoRegular,
    robotoBold: FONTS.robotoBold,
    robotoSlabMedium: FONTS.robotoSlabMedium,
    robotoSlabRegular: FONTS.robotoSlabRegular,
    robotoSlabBold: FONTS.robotoSlabBold,
  },
  fontsize: {
    xxs: SPACES[8],
    xs: SPACES[10],
    sm: SPACES[12],
    ssm: SPACES[13],
    reg: SPACES[14],
    cs: SPACES[16],
    md: SPACES[18],
    mmd: SPACES[20],
    lg: SPACES[24],
    xlg: SPACES[26],
    xl: SPACES[32],
    xxl: SPACES[36],
    xxl2: SPACES[40],
    xxl3: SPACES[50],
    xxl4: SPACES[56],
    xxl5: SPACES[64],
  },
  fontweight: {
    xlight: FONT_WEIGHT["xlight"],
    light: FONT_WEIGHT["light"],
    regular: FONT_WEIGHT["regular"],
    medium: FONT_WEIGHT["medium"],
    semiBold: FONT_WEIGHT["semiBold"],
    bold: FONT_WEIGHT["bold"],
  },
  breakpoints: {
    mobile: BREAKPOINT_VALUES[320],
    tablet: BREAKPOINT_VALUES[768],
    desktop: BREAKPOINT_VALUES[1024],
  },
  zIndex: {
    max: 9999,
    mid:99,
    min: -999,
  },
} as const;

export type Theme = typeof theme;
